import type { TFunction } from "i18next";

/** Canonical French city names used in URLs/API (moroccanCities). */
const CITY_SLUG_BY_CANONICAL: Record<string, string> = {
  Casablanca: "casablanca",
  Rabat: "rabat",
  Marrakech: "marrakech",
  "Fès": "fes",
  Tanger: "tanger",
  Agadir: "agadir",
  Meknès: "meknes",
  Oujda: "oujda",
  "Kénitra": "kenitra",
  "Tétouan": "tetouan",
  Safi: "safi",
  Mohammedia: "mohammedia",
  "El Jadida": "elJadida",
  "Béni Mellal": "beniMellal",
  Nador: "nador",
  Taza: "taza",
  "Khémisset": "khemisset",
  Larache: "larache",
  Khouribga: "khouribga",
  Settat: "settat",
  Essaouira: "essaouira",
  Ouarzazate: "ouarzazate",
  Chefchaouen: "chefchaouen",
  Ifrane: "ifrane",
  Errachidia: "errachidia",
};

const LOCATION_LABEL_SEP = " - ";

export interface LocationDisplayParts {
  city: string;
  address?: string;
}

/** Display label for UI; keeps canonical value for search/API. */
export const getLocalizedCityName = (canonicalName: string, t: TFunction): string => {
  if (!canonicalName.trim()) return canonicalName;
  const slug = CITY_SLUG_BY_CANONICAL[canonicalName];
  if (!slug) return canonicalName;
  const key = `search.cities.${slug}`;
  const translated = t(key);
  return translated && translated !== key ? translated : canonicalName;
};

/** UI-only: localized city (+ optional address). Booking payloads keep canonical city. */
export const formatLocalizedLocationDisplay = (
  loc: LocationDisplayParts,
  t: TFunction
): string => {
  const cityLabel = getLocalizedCityName(loc.city, t);
  const address = loc.address?.trim();
  return address ? `${cityLabel}${LOCATION_LABEL_SEP}${address}` : cityLabel;
};

/** UI-only: localizes city in stored "City" or "City - address" strings. */
export const localizeLocationDisplayString = (
  locationText: string,
  t: TFunction
): string => {
  const trimmed = locationText.trim();
  if (!trimmed) return locationText;
  const sepIdx = trimmed.indexOf(LOCATION_LABEL_SEP);
  if (sepIdx === -1) {
    return getLocalizedCityName(trimmed, t);
  }
  const city = trimmed.slice(0, sepIdx).trim();
  const rest = trimmed.slice(sepIdx + LOCATION_LABEL_SEP.length);
  return `${getLocalizedCityName(city, t)}${LOCATION_LABEL_SEP}${rest}`;
};

export interface ParsedLocationDisplay {
  city: string;
  detail: string;
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * UI-only split of a stored location. A known city stays on the first line;
 * the street, when present, stays on a second line. Booking still uses the raw string.
 */
export const parseLocationDisplay = (
  locationText: string,
  t: TFunction
): ParsedLocationDisplay => {
  const trimmed = locationText.trim();
  if (!trimmed) return { city: "", detail: "" };

  const match = Object.keys(CITY_SLUG_BY_CANONICAL)
    .sort((a, b) => b.length - a.length)
    .find((city) => trimmed.toLowerCase().includes(city.toLowerCase()));

  if (!match) return { city: trimmed, detail: "" };

  const detail = trimmed
    .replace(new RegExp(escapeRegExp(match), "i"), "")
    .replace(/^[\s,·-]+|[\s,·-]+$/g, "")
    .replace(/\s+-\s+/g, ", ")
    .trim();

  return { city: getLocalizedCityName(match, t), detail };
};

const STREET_TOKENS = new Set([
  "rue",
  "avenue",
  "ave",
  "boulevard",
  "route",
  "residence",
  "résidence",
  "airport",
  "entrance",
  "entree",
  "entrée",
  "immeuble",
  "lot",
  "numero",
  "numéro",
  "maroc",
  "morocco",
  "street",
]);

/**
 * Public cards show a city, or a neighborhood plus city.
 * Street numbers and road names stay off the card.
 */
export const formatPublicLocation = (locationText: string, t: TFunction): string => {
  const parsed = parseLocationDisplay(locationText, t);
  if (!parsed.city) return "";
  if (!parsed.detail) return parsed.city;

  const tokens = parsed.detail
    .replace(/\d+/g, " ")
    .split(/[\s,·/|.-]+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 2 && !STREET_TOKENS.has(token.toLowerCase()));

  const neighborhood = tokens[tokens.length - 1];
  if (!neighborhood) return parsed.city;

  const label = neighborhood.charAt(0).toUpperCase() + neighborhood.slice(1).toLowerCase();
  if (label.toLowerCase() === parsed.city.toLowerCase()) return parsed.city;
  return `${label}, ${parsed.city}`;
};

/** Match user query against canonical or localized city name. */
export const cityMatchesQuery = (
  canonicalName: string,
  query: string,
  t: TFunction
): boolean => {
  const q = query.toLowerCase().trim();
  if (!q) return true;
  if (canonicalName.toLowerCase().includes(q)) return true;
  return getLocalizedCityName(canonicalName, t).toLowerCase().includes(q);
};
